import {defineStore} from "pinia";
import {
    AcceptedDiscountOfferCommand,
    ApiClient, ApiError,
    CreateQpRuleCommand, DeleteQpRuleCommand,
    DiscountOfferStatus,
    InsertQpRulesFromTemplateCommand,
    InvoiceVM,
    RejectedDiscountOfferCommand,
    UpdateQpRuleCommand,
    UpdateVendorCommand,
    VendorDto,
} from "../../../clients/ApiClient";
import {api} from "@/clients/ApiClientService";
import router from "@/router";
import {errorToast, successToast} from "@/helpers/customToast";


export const useVendorStore = defineStore('vendorStore',{
    state: ()  => {
        return {
            vendorState: Object as Partial<VendorDto>,
            invoiceWithOnlyApprovalOffersState: Object as Partial<InvoiceVM>,
        }
    },
    
    actions : {
        clearVendor() {
            this.$state.vendorState = {} as Partial<VendorDto>;
        },

        async getVendor(vendorId: string | null | undefined): Promise<boolean> {
            this.clearVendor();
            if (!vendorId) {
                errorToast("This invoice does not have a valid vendor, so an offer cannot be created.");
                return false;
            }
            const res = await api.vendors.getVendor(vendorId).resolve();
            if (res.result !== undefined){
                this.$state.vendorState = res.result;
                return true;
            }
            errorToast("The vendor data required to create this offer could not be loaded.");
            return false;
        },
        
        async getInvoiceWithOnlyApprovalOffers <InvoiceVM>(invoiceId) {
            const res = await api.invoices.get(invoiceId).resolve();
            if (res.result !== undefined){
                res.result.InvoiceDiscountOffers = res.result.InvoiceDiscountOffers?.filter(function(e){
                    return e.Status === DiscountOfferStatus.PENDING_APPROVAL
                        || e.Status === DiscountOfferStatus.REJECTED
                        || e.Status === DiscountOfferStatus.ACCEPTED;
                });
                this.$state.invoiceWithOnlyApprovalOffersState = res.result;
            }
        },
        
        async rejectOffer<bool> (command : RejectedDiscountOfferCommand) {
            try {
                await api.invoices.rejectDiscountOffers(command);
                await this.getInvoiceWithOnlyApprovalOffers(command.InvoiceId);
                successToast();
                return true;
            } catch (error) {
                showOfferActionError(error);
                return false;
            }
        },


        async sendCredentials<bool> (vendorId : string) {
            
            const res = await api.vendors.sendCredentials(vendorId).resolve();
            if (res.succeed){
                successToast();
            }else{
                errorToast();
            }
            
            return res.succeed;
        },
        

        async editVendorDetails<bool> (command : UpdateVendorCommand) {
            const res = await api.vendors.updateVendor(command).resolve();
            if (res.succeed){
                await this.getVendor(command.Id);
                successToast();
            }else{
                errorToast();
            }
            return res.succeed;
        },
        
        async acceptOfferOption<bool> (command : AcceptedDiscountOfferCommand) {
            try {
                await api.invoices.acceptOffer(command);
                await this.getInvoiceWithOnlyApprovalOffers(command.InvoiceId);
                successToast();
                return true;
            } catch (error) {
                showOfferActionError(error);
                return false;
            }
        },

        
        async addVendorQpRule<bool> (command : CreateQpRuleCommand) {
            const res = await api.vendors.createQpRule(command).resolve();
            if (res.succeed){
                await this.getVendor(command.VendorId);
                successToast();
            }else{
                errorToast();
            }
            return res.succeed;
        },
        
        async editVendorQpRule<bool> (command : UpdateQpRuleCommand) {
            const res = await api.vendors.updateQpRule(command).resolve();
            if (res.succeed){
                successToast();
                await this.getVendor(command.VendorId);
            }else{
                errorToast();
            }
            return res.succeed;
        },
        
        async addQPpRulesFromTemplate <bool>(command : InsertQpRulesFromTemplateCommand) {
            const res = await api.vendors.insertFromTemplate(command).resolve();
            if (res.succeed){           
                successToast();
                await this.getVendor(command.VendorId);
            }else{
                errorToast();
            }
            return res.succeed;
        },
        
        async deleteVendorQpRule<bool> (command : DeleteQpRuleCommand) {
            const res = await api.vendors.deleteQpRule(command).resolve();
            if (res.succeed){
                successToast();
                await this.getVendor(command.VendorId);
            }else{
                errorToast();
            }
            return res.succeed;
        },
    },
    getters : {
        invoice: (state) => state,
    }
});

function showOfferActionError(error: unknown) {
    const apiError = error as ApiError;
    if (apiError.status === 403) {
        errorToast("This action requires the vendor account that received the offer.");
        return;
    }

    if (typeof apiError.body === "string" && apiError.body.trim()) {
        errorToast(apiError.body);
        return;
    }

    errorToast();
}
